// services/analyticsService.ts
import axios from 'axios';
import { ANALYTICS_API_URL } from '../constants/config';
import { getAndSanitize } from '../shared/utils/sanitization';
import type { UserCategoryDetails, AnalyticsFilters } from '../types/analytics';

export interface UserDetailsRequest {
  category: 'total' | 'usersWithDeposits' | 'whales' | 'dolphins' | 'fish' | 'inactive';
  dateRange: {
    start: string;
    end: string;
  };
  adminId?: string;
  accountName?: string;
  limit?: number;
  offset?: number;
}

export class AnalyticsService {
  private static getAuthHeaders() {
    const token = getAndSanitize('token');
    
    if (!token) {
      throw new Error('No hay token de autenticación disponible. Por favor, inicia sesión nuevamente.');
    }
    
    // Validar que el token no esté vacío o malformado
    if (typeof token !== 'string' || token.trim() === '' || token === 'null' || token === 'undefined') {
      throw new Error('Token de autenticación inválido. Por favor, inicia sesión nuevamente.');
    }
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Obtener detalles de usuarios por categoría
   */
  static async getUserCategoryDetails(
    category: string,
    filters: AnalyticsFilters,
    limit: number = 50,
    offset: number = 0
  ): Promise<UserCategoryDetails> {
    try {
      const requestData: UserDetailsRequest = {
        category: category as any,
        dateRange: filters.dateRange,
        adminId: filters.adminId,
        accountName: filters.accountName,
        limit,
        offset
      };


      const response = await axios.post(
        `${ANALYTICS_API_URL}/api/analytics/user-details/${category}`,
        requestData,
        {
          headers: this.getAuthHeaders()
        }
      );

      if (response.data.success) {
        
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al obtener detalles de usuarios');
      }
    } catch (error) {
      console.error('❌ [Analytics Service] Error en getUserCategoryDetails:', error);
      
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const errorMessage = error.response?.data?.message || error.message;
        
        // Manejar errores específicos
        if (status === 401) {
          throw new Error('Token de autenticación inválido. Por favor, inicia sesión nuevamente.');
        } else if (status === 404) {
          throw new Error('Endpoint no encontrado. Verifica que el servidor esté actualizado.');
        } else if (status === 403) {
          throw new Error('No tienes permisos para acceder a estos datos.');
        }
        
        throw new Error(`Error al obtener detalles: ${errorMessage}`);
      }
      
      // Si el error es de autenticación local
      if (error instanceof Error && error.message.includes('token')) {
        throw error; // Re-lanzar el error de token tal como está
      }
      
      throw new Error('Error de conexión al obtener detalles de usuarios');
    }
  }

  /**
   * Exportar detalles de usuarios a Excel/CSV
   */
  static async exportUserCategoryDetails(
    category: string,
    filters: AnalyticsFilters,
    format: 'xlsx' | 'csv' = 'xlsx'
  ): Promise<Blob> {
    try {
      const requestData: UserDetailsRequest = {
        category: category as any,
        dateRange: filters.dateRange,
        adminId: filters.adminId,
        accountName: filters.accountName,
        limit: 10000, // Exportar todos los datos
        offset: 0
      };


      const response = await axios.post(
        `${ANALYTICS_API_URL}/api/analytics/user-details/${category}/export`,
        { ...requestData, format },
        {
          headers: this.getAuthHeaders(),
          responseType: 'blob'
        }
      );

      
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics Service] Error en exportUserCategoryDetails:', error);
      
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(`Error al exportar: ${errorMessage}`);
      }
      
      throw new Error('Error de conexión al exportar detalles de usuarios');
    }
  }

  /**
   * Descargar archivo de exportación
   */
  static downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Obtener operaciones de un usuario específico
   */
  static async getUserOperations(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<{
    operations: any[];
    totalCount: number;
    pagination: {
      limit: number;
      offset: number;
      hasMore: boolean;
      totalPages: number;
      currentPage: number;
    };
  }> {
    try {

      const response = await axios.get(
        `${ANALYTICS_API_URL}/api/analytics/user-operations/${userId}`,
        {
          params: { limit, offset },
          headers: this.getAuthHeaders()
        }
      );

      if (response.data.success) {
        
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al obtener operaciones');
      }

    } catch (error) {
      console.error('❌ [Analytics Service] Error en getUserOperations:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        }
        if (error.response?.status === 403) {
          throw new Error('No tienes permisos para ver las operaciones de este usuario.');
        }
        if (error.response?.status === 404) {
          throw new Error('Usuario no encontrado.');
        }
      }
      
      throw new Error(`Error al obtener operaciones: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }
  }
}